import { ref } from 'vue';
import { useQuasar } from 'quasar';

const registration = ref<ServiceWorkerRegistration | null>(null);
const updateAvailable = ref<boolean>(false);
const isChecking = ref<boolean>(false);
const isOffline = ref<boolean>(typeof navigator !== 'undefined' ? !navigator.onLine : false);

const UPDATE_SUCCESS_STORAGE_KEY = 'steps_pwa_update_successful';
let isInitialized = false;

export function usePwaUpdate() {
  const $q = useQuasar();

  function promptUpdate() {
    $q.dialog({
      title: 'Aktualisierung verfügbar',
      message: 'Eine neuere Version der App ist verfügbar. Möchtest Du die App jetzt aktualisieren?',
      ok: {
        label: 'Jetzt aktualisieren',
        color: 'primary',
      },
      cancel: {
        label: 'Später',
        flat: true,
      },
      persistent: false,
    }).onOk(() => {
      applyUpdate();
    });
  }

  function applyUpdate() {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(UPDATE_SUCCESS_STORAGE_KEY, 'true');
    } catch {
      // ignore storage errors
    }

    const reg = registration.value || window.__PWA_REGISTRATION__;
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Controller change listener will reload, but fallback just in case
      setTimeout(() => {
        window.location.reload();
      }, 1200);
    } else {
      window.location.reload();
    }
  }

  async function checkForUpdates(manual = false): Promise<boolean> {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return false;
    }

    if (!navigator.onLine) {
      isOffline.value = true;
      if (manual) {
        $q.notify({
          type: 'warning',
          message: 'Keine Internetverbindung vorhanden. Prüfen auf neue Version nicht möglich.',
          icon: 'wifi_off',
          position: 'top',
        });
      }
      return false;
    }

    isOffline.value = false;
    isChecking.value = true;

    try {
      // Always query fresh registration from navigator if available
      let reg: ServiceWorkerRegistration | null | undefined = null;

      if ('serviceWorker' in navigator) {
        const swReg = await navigator.serviceWorker.getRegistration();
        if (swReg) {
          reg = swReg;
          registration.value = swReg;
        }
      }

      if (!reg) {
        reg = registration.value || window.__PWA_REGISTRATION__;
      }

      if (!reg || (!reg.active && !reg.waiting && !reg.installing)) {
        if (manual) {
          $q.notify({
            type: 'info',
            message: 'Die App ist auf dem aktuellen Stand.',
            icon: 'check_circle',
            position: 'top',
          });
        }
        isChecking.value = false;
        return false;
      }

      // If already a waiting worker
      if (reg.waiting) {
        updateAvailable.value = true;
        isChecking.value = false;
        promptUpdate();
        return true;
      }

      // Check for update from server
      try {
        await reg.update();
      } catch (updateErr: unknown) {
        // If update failed due to invalid state (e.g. redundant/uninstalled worker), re-check registration
        if (
          updateErr instanceof Error &&
          (updateErr.name === 'InvalidStateError' || updateErr.message.includes('invalid state'))
        ) {
          if ('serviceWorker' in navigator) {
            const freshReg = await navigator.serviceWorker.getRegistration();
            if (freshReg && (freshReg.active || freshReg.waiting || freshReg.installing)) {
              registration.value = freshReg;
              reg = freshReg;
              await reg.update();
            } else {
              throw updateErr;
            }
          } else {
            throw updateErr;
          }
        } else {
          throw updateErr;
        }
      }

      // Give worker a moment to process updatefound/installing
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (reg.waiting) {
        updateAvailable.value = true;
        isChecking.value = false;
        promptUpdate();
        return true;
      }

      if (reg.installing) {
        const worker = reg.installing;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && reg.waiting) {
            updateAvailable.value = true;
            promptUpdate();
          }
        });
        if (manual) {
          $q.notify({
            type: 'info',
            message: 'Eine neue Version wird heruntergeladen...',
            icon: 'cloud_download',
            position: 'top',
          });
        }
        isChecking.value = false;
        return true;
      }

      if (manual) {
        $q.notify({
          type: 'positive',
          message: 'Die App ist bereits auf dem neuesten Stand.',
          icon: 'check_circle',
          position: 'top',
        });
      }
      isChecking.value = false;
      return false;
    } catch (err) {
      console.error('Update check failed:', err);
      if (manual) {
        $q.notify({
          type: 'negative',
          message: 'Fehler beim Prüfen auf Aktualisierungen.',
          icon: 'error',
          position: 'top',
        });
      }
      isChecking.value = false;
      return false;
    }
  }

  function initPwaUpdate() {
    if (typeof window === 'undefined' || isInitialized) return;
    isInitialized = true;

    // Check if app was just updated
    try {
      if (localStorage.getItem(UPDATE_SUCCESS_STORAGE_KEY) === 'true') {
        localStorage.removeItem(UPDATE_SUCCESS_STORAGE_KEY);
        $q.notify({
          type: 'positive',
          message: 'Die App wurde erfolgreich auf die neueste Version aktualisiert.',
          icon: 'check_circle',
          position: 'top',
          timeout: 4000,
        });
      }
    } catch {
      // ignore
    }

    // Set existing registration if available
    if (window.__PWA_REGISTRATION__) {
      registration.value = window.__PWA_REGISTRATION__;
    } else if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          registration.value = reg;
          if (reg.waiting) {
            updateAvailable.value = true;
            promptUpdate();
          }
        }
      });
    }

    // Listen for custom SW events
    window.addEventListener('pwa-updated', (event: Event) => {
      const customEvent = event as CustomEvent<ServiceWorkerRegistration>;
      if (customEvent.detail) {
        registration.value = customEvent.detail;
      }
      updateAvailable.value = true;
      promptUpdate();
    });

    window.addEventListener('pwa-ready', (event: Event) => {
      const customEvent = event as CustomEvent<ServiceWorkerRegistration>;
      if (customEvent.detail) {
        registration.value = customEvent.detail;
      }
    });

    window.addEventListener('pwa-registered', (event: Event) => {
      const customEvent = event as CustomEvent<ServiceWorkerRegistration>;
      if (customEvent.detail) {
        registration.value = customEvent.detail;
      }
    });

    // Listen for online / offline events
    window.addEventListener('online', () => {
      isOffline.value = false;
      void checkForUpdates(false);
    });

    window.addEventListener('offline', () => {
      isOffline.value = true;
    });

    // When returning to tab/app, check for updates if online
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        void checkForUpdates(false);
      }
    });

    // Reload when controller changes after skipWaiting
    if ('serviceWorker' in navigator) {
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    }
  }

  return {
    registration,
    updateAvailable,
    isChecking,
    isOffline,
    checkForUpdates,
    applyUpdate,
    promptUpdate,
    initPwaUpdate,
  };
}
